import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import { listAdminDepartments, assignDepartmentToAdmin, revokeDepartmentFromAdmin } from '../../api/admin';
import { DEPARTMENTS } from '../../auth/permissions';
import { extractErrorMessage } from '../../api/client';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorBanner from '../../components/ui/ErrorBanner';

export default function DepartmentsModal({ open, user, onClose }) {
  const qc = useQueryClient();

  const { data: userDepts, isLoading, error } = useQuery({
    queryKey: ['admin-departments', user?.id],
    queryFn: () => listAdminDepartments(user.id),
    enabled: !!user?.id,
  });

  const assign = useMutation({
    mutationFn: (dept) => assignDepartmentToAdmin(user.id, dept),
    onSuccess: () => {
      toast.success('Department assigned');
      qc.invalidateQueries({ queryKey: ['admin-departments', user.id] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const revoke = useMutation({
    mutationFn: (dept) => revokeDepartmentFromAdmin(user.id, dept),
    onSuccess: () => {
      toast.success('Department revoked');
      qc.invalidateQueries({ queryKey: ['admin-departments', user.id] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const handleToggle = (dept, isAssigned) => {
    if (isAssigned) {
      revoke.mutate(dept);
    } else {
      assign.mutate(dept);
    }
  };

  const isWorking = assign.isPending || revoke.isPending;
  const currentDepts = userDepts || [];

  return (
    <Modal 
      open={open} 
      onClose={onClose} 
      title={`Department Access: ${user?.username || ''}`}
      footer={
        <div className="flex justify-end">
          <Button onClick={onClose}>Save</Button>
        </div>
      }
    >
      <div className="p-4">
        <p className="mb-4 text-sm text-ink-muted">
          Granting department access to an ADMIN allows them to approve workflows, view department budgets, and manage resources for that specific department.
        </p>

        {isLoading ? (
          <LoadingSpinner label="Loading departments..." />
        ) : error ? (
          <ErrorBanner error={error} title="Failed to load departments" />
        ) : (
          <div className="space-y-3">
            {Object.values(DEPARTMENTS).map((dept) => {
              const isAssigned = currentDepts.includes(dept);
              return (
                <label
                  key={dept}
                  className="flex items-center justify-between rounded-lg border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={isAssigned}
                      onChange={() => handleToggle(dept, isAssigned)}
                      disabled={isWorking}
                      className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30 disabled:opacity-50"
                    />
                    <span className="font-medium text-ink">{dept}</span>
                  </div>
                  {isAssigned && (
                    <span className="text-xs font-semibold text-success">Granted</span>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
